/**
 * Visibility hierarchy for Education Observations.
 *
 * An observation record is visible only to someone STRICTLY ABOVE the observed
 * teacher's level:
 *
 *   observed = Teaching Team  -> Senior Teacher, Lead Teacher, Admin/Management
 *   observed = Senior Teacher -> Lead Teacher, Admin/Management
 *   observed = Lead Teacher   -> Admin/Management
 *
 * Observers are drawn only from Senior and Lead Teachers (Admin/Management can
 * read observations but do not carry them out), and must still sit above the
 * teacher being observed.
 *
 * NOTE: a member of the Teaching Team is never a viewer in this model — they are
 * observed, not an observer — so they see an empty list. To let people read the
 * observations written about themselves, relax `canView()` with an owner check.
 */

export enum ObservationLevel {
  NONE = 0,
  TEACHING_TEAM = 1,
  SENIOR_TEACHER = 2,
  LEAD_TEACHER = 3,
  ADMIN = 4,
}

/** Values persisted in the `teacherType` field of an observation document. */
export const TEACHER_TYPE_TEACHING_TEAM = 'Teaching Team';
export const TEACHER_TYPE_SENIOR_TEACHER = 'Senior Teacher';
export const TEACHER_TYPE_LEAD_TEACHER = 'Lead Teacher';

/**
 * `users/{uid}/role` values mapped to a level. Roles absent from this map get
 * ObservationLevel.NONE and therefore see nothing — extend the map to grant a
 * role access.
 */
const ROLE_LEVELS: { [role: string]: ObservationLevel } = {
  'teacher': ObservationLevel.TEACHING_TEAM,
  'teaching team': ObservationLevel.TEACHING_TEAM,
  'sr teacher': ObservationLevel.SENIOR_TEACHER,
  'senior teacher': ObservationLevel.SENIOR_TEACHER,
  'lead teacher': ObservationLevel.LEAD_TEACHER,
  'admin': ObservationLevel.ADMIN,
  'super admin': ObservationLevel.ADMIN,
  'superadmin': ObservationLevel.ADMIN,
  'management': ObservationLevel.ADMIN,
};

const TEACHER_TYPE_LEVELS: { [teacherType: string]: ObservationLevel } = {
  'teaching team': ObservationLevel.TEACHING_TEAM,
  'teacher': ObservationLevel.TEACHING_TEAM,
  'senior teacher': ObservationLevel.SENIOR_TEACHER,
  'sr teacher': ObservationLevel.SENIOR_TEACHER,
  'lead teacher': ObservationLevel.LEAD_TEACHER,
};

const normalise = (value?: string | null) => (value || '').trim().toLowerCase();

/** Role strings are inconsistently cased in the user records, so match case-insensitively. */
export function levelFromRole(role?: string | null, department?: string | null): ObservationLevel {
  if (normalise(department) === 'management') {
    return ObservationLevel.ADMIN;
  }
  return ROLE_LEVELS[normalise(role)] ?? ObservationLevel.NONE;
}

export function levelFromTeacherType(teacherType?: string | null): ObservationLevel {
  return TEACHER_TYPE_LEVELS[normalise(teacherType)] ?? ObservationLevel.NONE;
}

/** The `teacherType` to persist for a teacher holding `role`, or '' for a non-teaching role. */
export function teacherTypeFromRole(role?: string | null): string {
  return teacherTypeFromLevel(levelFromRole(role));
}

/** Canonical `teacherType` label for a level, or '' when outside the hierarchy. */
export function teacherTypeFromLevel(level: ObservationLevel): string {
  switch (level) {
    case ObservationLevel.TEACHING_TEAM:
      return TEACHER_TYPE_TEACHING_TEAM;
    case ObservationLevel.SENIOR_TEACHER:
      return TEACHER_TYPE_SENIOR_TEACHER;
    case ObservationLevel.LEAD_TEACHER:
      return TEACHER_TYPE_LEAD_TEACHER;
    default:
      return '';
  }
}

export function canView(viewerLevel: ObservationLevel, observedLevel: ObservationLevel): boolean {
  return observedLevel !== ObservationLevel.NONE && viewerLevel > observedLevel;
}

/** Level of the user currently signed in, from the `userData` localStorage record. */
export function getCurrentUserLevel(): ObservationLevel {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return levelFromRole(userData?.role, userData?.department);
  } catch {
    return ObservationLevel.NONE;
  }
}

/** userName (lower-cased) -> level, built from the live user directory. */
export function buildDirectoryLevels(users: Array<{ userName?: string; role?: string; department?: string }>) {
  const levels = new Map<string, ObservationLevel>();
  (users || []).forEach(user => {
    const name = normalise(user?.userName);
    if (name) {
      levels.set(name, levelFromRole(user?.role, user?.department));
    }
  });
  return levels;
}

/**
 * Level of the teacher an observation was written about.
 *
 * The live directory wins over the stored `teacherType`: records created before
 * Senior Teacher existed as a separate type stored those teachers as
 * 'Lead Teacher', which would over-restrict them.
 */
export function observedLevelOf(
  observation: { teacherName?: string; teacherType?: string },
  directoryLevels: Map<string, ObservationLevel>
): ObservationLevel {
  const fromDirectory = directoryLevels.get(normalise(observation?.teacherName)) ?? ObservationLevel.NONE;
  return fromDirectory !== ObservationLevel.NONE
    ? fromDirectory
    : levelFromTeacherType(observation?.teacherType);
}
